(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  const closeMenu = (menu) => {
    const details = menu.querySelector('details');
    const summary = menu.querySelector('summary');
    if (!details) return;
    details.removeAttribute('open');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  };

  const initialiseMenus = () => {
    document.querySelectorAll('.senfon-header-menu').forEach((menu) => {
      if (menu.dataset.senfonMenuReady === 'true') return;

      const details = menu.querySelector('details');
      const summary = menu.querySelector('summary');
      if (!details || !summary) return;

      let closeTimer;
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
      });

      const openMenu = () => {
        if (!desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        document.querySelectorAll('.senfon-header-menu').forEach((otherMenu) => {
          if (otherMenu !== menu) closeMenu(otherMenu);
        });
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      };

      const scheduleClose = () => {
        if (!desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => closeMenu(menu), 180);
      };

      menu.addEventListener('pointerenter', openMenu);
      menu.addEventListener('pointerleave', scheduleClose);
      menu.addEventListener('focusin', openMenu);
      menu.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!menu.contains(document.activeElement)) scheduleClose();
        }, 0);
      });

      summary.addEventListener('click', (event) => {
        if (!desktopQuery.matches) return;
        event.preventDefault();
        if (details.open) {
          closeMenu(menu);
        } else {
          openMenu();
        }
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenu(menu);
          summary.focus();
        }
      });

      menu.dataset.senfonMenuReady = 'true';
    });
  };

  const syncMobileDrawerState = (details) => {
    const isVisuallyOpen = details.hasAttribute('open') && details.classList.contains('menu-opening');
    document.body.classList.toggle('senfon-mobile-menu-open', isVisuallyOpen);
  };

  const initialiseMobileDrawer = () => {
    document.querySelectorAll('header-drawer .menu-drawer-container').forEach((details) => {
      if (details.dataset.senfonDrawerReady === 'true') return;

      const observer = new MutationObserver(() => syncMobileDrawerState(details));
      observer.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });
      details.addEventListener('toggle', () => syncMobileDrawerState(details));
      syncMobileDrawerState(details);
      details.dataset.senfonDrawerReady = 'true';
    });
  };

  const closeMobileDrawer = (trigger) => {
    const headerDrawer = trigger.closest('header-drawer');
    if (!headerDrawer) return Promise.resolve();

    const details = headerDrawer.querySelector(':scope > details');
    const summary = details ? details.querySelector(':scope > summary') : null;
    if (!details || !details.hasAttribute('open')) return Promise.resolve();

    if (typeof headerDrawer.closeMenuDrawer === 'function' && summary) {
      headerDrawer.closeMenuDrawer(new Event('click'), summary);
    } else {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      document.body.classList.remove('senfon-mobile-menu-open', 'overflow-hidden-tablet', 'overflow-hidden-mobile');
      document.querySelector('.section-header')?.classList.remove('menu-open');
    }

    return new Promise((resolve) => window.setTimeout(resolve, 430));
  };

  const normaliseCatalogUrl = (rawUrl) => {
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      if (parsed.hostname === 'cdn.shopify.com' && parsed.pathname.includes('/files/')) {
        const fileName = parsed.pathname.split('/files/').pop();
        return `${window.location.origin}/cdn/shop/files/${fileName}${parsed.search}`;
      }
      return parsed.href;
    } catch (error) {
      return rawUrl;
    }
  };

  const triggerBrowserDownload = (url, filename) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.hidden = true;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const directCatalogDownload = (button, dialog) => {
    const url = normaliseCatalogUrl(button.dataset.senfonCatalogUrl);
    const filename = button.dataset.senfonCatalogFilename || 'senfon-product-catalog.pdf';
    const label = button.querySelector('[data-senfon-catalog-button-label]');
    const status = dialog.querySelector('[data-senfon-catalog-status]');
    const originalLabel = label ? label.textContent : 'Download';

    if (!url || button.disabled) return;

    document.dispatchEvent(new CustomEvent('senfon:catalog-download'));
    if (window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish('senfon:catalog_download_confirmed', { source: 'header_catalog' });
    }

    triggerBrowserDownload(url, filename);
    if (status) status.textContent = button.dataset.senfonCatalogStatusStarted || '';
    if (label) label.textContent = originalLabel;
    window.setTimeout(() => {
      if (dialog.open) dialog.close('download');
    }, 350);
  };

  const initialiseCatalogDownloads = () => {
    document.querySelectorAll('[data-senfon-catalog-open]').forEach((trigger) => {
      if (trigger.dataset.senfonCatalogReady === 'true') return;

      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        const dialogId = trigger.getAttribute('data-senfon-catalog-open');
        const dialog = dialogId ? document.getElementById(dialogId) : null;
        if (!dialog || typeof dialog.showModal !== 'function') return;

        await closeMobileDrawer(trigger);
        dialog.showModal();
        document.dispatchEvent(new CustomEvent('senfon:catalog-open'));
        if (window.Shopify?.analytics?.publish) {
          Shopify.analytics.publish('senfon:catalog_download_click', { source: 'header_catalog' });
        }
      });

      trigger.dataset.senfonCatalogReady = 'true';
    });

    document.querySelectorAll('[data-senfon-catalog-dialog]').forEach((dialog) => {
      if (dialog.dataset.senfonCatalogReady === 'true') return;

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close('cancel');
      });

      const confirmButton = dialog.querySelector('[data-senfon-catalog-confirm]');
      if (confirmButton) {
        confirmButton.addEventListener('click', () => directCatalogDownload(confirmButton, dialog));
      }

      dialog.addEventListener('close', () => {
        const status = dialog.querySelector('[data-senfon-catalog-status]');
        if (status) status.textContent = '';
      });

      dialog.dataset.senfonCatalogReady = 'true';
    });
  };

  const closeOnBreakpointChange = () => {
    if (!desktopQuery.matches) {
      document.querySelectorAll('.senfon-header-menu').forEach(closeMenu);
    } else {
      document.body.classList.remove('senfon-mobile-menu-open');
    }
  };

  const initialiseAll = () => {
    initialiseMenus();
    initialiseMobileDrawer();
    initialiseCatalogDownloads();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  desktopQuery.addEventListener('change', closeOnBreakpointChange);
  document.addEventListener('shopify:section:load', initialiseAll);
})();
